import { Button, Input, Modal, Typography } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AiOutlineArrowLeft,
  AiOutlineCheckCircle,
  AiOutlineSetting,
} from "react-icons/ai";
import { useNavigate } from "react-router";

import type { Client } from "generated/api/v2Internal/models/client";

import { useCustomNotification } from "design-system/hooks";
import { useListClientsDirect } from "generated/api/v2Internal/client-lifecycle/client-lifecycle";
import { pathsSchema } from "shell/platform/config/paths";
import { useClientScoping } from "shell/platform/contexts/ClientScopingContext";

import s from "../shared/ConfigureModal/ConfigureModal.module.css";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

type Step = "msa" | "sub-client";

export const ClientConfigurationModal = ({ isOpen, onClose }: Props) => {
  const { t } = useTranslation(["common"]);
  const navigate = useNavigate();
  const { showNotification } = useCustomNotification();
  const {
    hierarchyLevel,
    msaClientId,
    clientId,
    availableMsaClients,
    availableSubClients,
    onMsaClientChange,
    onSubClientChange,
  } = useClientScoping();

  const isInternal = hierarchyLevel === "INTERNAL";

  const [step, setStep] = useState<Step>(isInternal ? "msa" : "sub-client");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMsaId, setSelectedMsaId] = useState<string>(msaClientId ?? "");
  const [selectedSubClientId, setSelectedSubClientId] =
    useState<string>(clientId);

  const msaChanged = selectedMsaId !== (msaClientId ?? "");

  const { data: localSubClientsData } = useListClientsDirect(
    { type: "SUB", parentId: selectedMsaId },
    { query: { enabled: msaChanged && !!selectedMsaId } },
  );

  const msaOptions = useMemo(
    () =>
      (availableMsaClients as Client[]).map(c => ({
        id: c.id ?? "",
        name: c.name ?? c.id ?? "",
      })),
    [availableMsaClients],
  );

  const localSubClients = localSubClientsData?.data?.clients as
    | Client[]
    | undefined;

  const subClientOptions = useMemo(() => {
    const source: Client[] =
      msaChanged && localSubClients
        ? localSubClients
        : (availableSubClients as Client[]);
    return source
      .filter(c => Boolean(c.id))
      .map(c => ({ id: c.id ?? "", name: c.name ?? c.id ?? "" }));
  }, [msaChanged, localSubClients, availableSubClients]);

  const allOptions = step === "msa" ? msaOptions : subClientOptions;
  const currentValue = step === "msa" ? selectedMsaId : selectedSubClientId;

  const currentOptions = useMemo(() => {
    if (!searchTerm) return allOptions;
    const term = searchTerm.toLowerCase();
    return allOptions.filter(
      o =>
        o.name.toLowerCase().includes(term) ||
        o.id.toLowerCase().includes(term),
    );
  }, [allOptions, searchTerm]);
  const typeLabel =
    step === "msa"
      ? t("labels.client")
      : t("components.clientScoping.typeSubClient");

  const title =
    step === "msa"
      ? t("components.clientScoping.selectClient")
      : t("components.clientScoping.selectSubClient");

  const handleSelect = (id: string) => {
    if (step === "msa") {
      setSelectedMsaId(id);
    } else {
      setSelectedSubClientId(id);
    }
  };

  const handleSave = () => {
    if (step === "msa" && isInternal) {
      setStep("sub-client");
      setSelectedSubClientId("");
      setSearchTerm("");
      return;
    }

    if (selectedMsaId !== (msaClientId ?? "")) {
      onMsaClientChange(selectedMsaId);
    }
    onSubClientChange(selectedSubClientId || null);

    showNotification({
      type: "success",
      message: t("status.success"),
      description: t("components.clientScoping.successDescription", {
        type: typeLabel,
      }),
    });

    onClose();
    void navigate(pathsSchema.portal.index);
  };

  const isSaveDisabled = step === "msa" ? !selectedMsaId : !selectedSubClientId;

  const saveLabel =
    step === "msa" && isInternal
      ? t("components.buttons.next")
      : t("components.buttons.saveChanges");

  return (
    <Modal
      title={
        <header className={s.header}>
          <AiOutlineSetting size={25} /> <span>{title}</span>
        </header>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={700}
    >
      <section className={s.container}>
        {step === "sub-client" && isInternal && (
          <Button
            type="link"
            icon={<AiOutlineArrowLeft />}
            onClick={() => {
              setStep("msa");
              setSearchTerm("");
            }}
            className={s.backButton}
          >
            {t("components.clientScoping.backToClients")}
          </Button>
        )}
        <p>
          {step === "msa"
            ? t("components.clientScoping.selectClientDescription")
            : t("components.clientScoping.selectSubClientDescription")}
        </p>
        <Input.Search
          placeholder={t("components.clientScoping.searchPlaceholder")}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          allowClear
        />
        <div className={s.options}>
          <div className={s.cards}>
            {currentOptions.map(data => (
              <section className={s.card} key={data.id}>
                <p>{typeLabel}</p>
                <Typography.Text strong>{data.name}</Typography.Text>
                <section>
                  {data.id === currentValue ? (
                    <p className={s.activeTag}>
                      <AiOutlineCheckCircle className={s.circleCheckIcon} />
                      {t("status.active")} {typeLabel}
                    </p>
                  ) : (
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => handleSelect(data.id)}
                      className={s.setDefaultBtn}
                      data-testid={`option-card-${data.name}`}
                    >
                      {t("components.clientScoping.configureAsDefault")}{" "}
                      {typeLabel}
                    </Button>
                  )}
                </section>
              </section>
            ))}
          </div>
        </div>
      </section>
      <section className={s.actions}>
        <Button
          size="small"
          type="primary"
          onClick={handleSave}
          disabled={isSaveDisabled}
          className={s.saveBtn}
          data-testid="saveButton"
        >
          {saveLabel}
        </Button>
      </section>
    </Modal>
  );
};
